import type { ReactNode } from "react";
import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";
import { cn } from "@/utils/cn";

interface CardSlotProps {
    title: string;
    subtitle?: string;
    selected?: boolean;
    locked?: boolean;
    onClick?: () => void;
    children?: ReactNode;
    className?: string;
    /** 프레임 색 (예: ember for primary, parchmentDim for default). */
    tone?: "default" | "primary" | "highlight";
    width?: number | string;
    height?: number | string;
}

/**
 * 영혼·이야기·운명 카드 선택의 홀더 프레임. 카드 자체의 시각 표현은 children 으로 위임.
 */
export function CardSlot({
    title,
    subtitle,
    selected,
    locked,
    onClick,
    children,
    className,
    tone = "default",
    width,
    height,
}: CardSlotProps) {
    const borderColor = selected
        ? tokens.color.ember
        : tone === "highlight"
            ? tokens.color.brass
            : tokens.color.parchmentDim;

    return (
        <motion.button
            whileHover={locked ? undefined : { scale: 1.025, y: -5 }}
            whileTap={locked ? undefined : { scale: 0.98 }}
            animate={selected
                ? {
                    boxShadow: [
                        `0 0 22px ${tokens.color.ember}36`,
                        `0 0 36px ${tokens.color.ember}55`,
                        `0 0 22px ${tokens.color.ember}36`,
                    ],
                    y: [0, -2, 0],
                }
                : {
                    boxShadow: `inset 0 0 48px ${tokens.color.parchmentDim}14`,
                    y: 0,
                }}
            transition={selected
                ? { boxShadow: { duration: 2.8, repeat: Infinity, ease: "easeInOut" }, y: { duration: 2.8, repeat: Infinity, ease: "easeInOut" } }
                : { duration: 0.2 }}
            disabled={locked}
            onClick={onClick}
            className={cn("relative rounded-l overflow-hidden text-left flex-shrink-0", className)}
            style={{
                width,
                height,
                border: `1px solid ${borderColor}`,
                background: selected
                    ? `${tokens.color.ember}12`
                    : `${tokens.color.ink}CC`,
                boxShadow: selected
                    ? `0 0 36px ${tokens.color.ember}55`
                    : `inset 0 0 48px ${tokens.color.parchmentDim}14`,
                opacity: locked ? 0.45 : 1,
                cursor: locked ? "not-allowed" : "pointer",
            }}
        >
            {/* 2026-04-23: 좌측 tone accent — Spirytale 언어 통일. 선택 시 ember 로 격상. */}
            <div
                aria-hidden
                className="absolute inset-y-0 left-0"
                style={{
                    width: 2,
                    background: `linear-gradient(180deg, ${borderColor}FF 0%, ${borderColor}44 100%)`,
                    boxShadow: selected ? `0 0 12px ${tokens.color.ember}CC` : `0 0 6px ${borderColor}66`,
                }}
            />
            {selected && (
                <motion.div
                    aria-hidden
                    className="absolute inset-y-0 -left-1/3 w-1/2"
                    animate={{ x: ["0%", "230%"] }}
                    transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut", repeatDelay: 0.6 }}
                    style={{
                        background: `linear-gradient(90deg, transparent 0%, ${tokens.color.parchment}14 52%, transparent 100%)`,
                        transform: "skewX(-18deg)",
                        pointerEvents: "none",
                    }}
                />
            )}
            <div className="p-m flex flex-col gap-xs">
                <div
                    className="font-display"
                    style={{
                        color: selected ? tokens.color.ember : tokens.color.parchment,
                        fontSize: tokens.type.size.subtitle,
                        letterSpacing: tokens.type.letterSpacing.wide,
                        lineHeight: 1.2,
                    }}
                >
                    {title}
                </div>
                {subtitle && (
                    <div
                        className="font-body"
                        style={{
                            color: tokens.color.parchmentDim,
                            fontSize: tokens.type.size.small,
                            lineHeight: tokens.type.lineHeight.snug,
                        }}
                    >
                        {subtitle}
                    </div>
                )}
                {children && <div className="mt-xs">{children}</div>}
            </div>
            {locked && (
                <div
                    className="absolute top-m right-m rounded-pill px-s py-[2px] font-body flex items-center gap-[4px]"
                    style={{
                        color: tokens.color.burgundy,
                        background: `${tokens.color.ink}CC`,
                        border: `1px solid ${tokens.color.burgundy}88`,
                        boxShadow: `0 0 10px ${tokens.color.burgundy}55`,
                        fontSize: 10,
                        letterSpacing: "0.22em",
                        textTransform: "uppercase",
                    }}
                >
                    <span aria-hidden style={{ fontSize: 10 }}>✕</span>
                    Locked · 차단됨
                </div>
            )}
        </motion.button>
    );
}
